import React, { useState, useMemo, useEffect } from 'react';
import { Goal, Milestone, Action, GoalNode, MilestoneNode, ActionNode, GoalType as GoalTypeEnum } from '../types';
import InlineEditableText from './InlineEditableText';
import InlineEditableDate from './InlineEditableDate';
import CompletionDot from './CompletionDot';
import CollapseChevron from './CollapseChevron';
import MilestoneRow from './MilestoneRow';
import ActionRow from './ActionRow';
import PhaseIndicator, { GoalPhase, detectCurrentPhase } from './PhaseIndicator';
import { useFocusDetection, MILESTONE_CATEGORY_LABELS } from '../focus';
import ExecutionReadyCard from '../sparring/ExecutionReadyCard';
import { GoalIcon, WarningIcon, SuccessIcon } from '../../../icons';

// Support both Firebase types and legacy Node types for backward compatibility
type GoalDataType = Goal | GoalNode;
type MilestoneDataType = Milestone | MilestoneNode;
type ActionDataType = Action | ActionNode;

// Goal type badge colors and labels
const GOAL_TYPE_BADGE: Record<GoalTypeEnum, { label: string; color: string }> = {
  A: { label: 'A', color: 'bg-green-100 text-green-700' },
  B: { label: 'B', color: 'bg-blue-100 text-blue-700' },
  C: { label: 'C', color: 'bg-purple-100 text-purple-700' },
};

interface GoalCardProps {
  goal: GoalDataType;
  milestones: MilestoneDataType[];
  actions: ActionDataType[];
  onUpdateGoal: (id: string, updates: Partial<GoalDataType>) => void;
  onUpdateMilestone: (id: string, updates: Partial<MilestoneDataType>) => void;
  onToggleAction: (id: string) => void;
  onUpdateAction: (id: string, title: string) => void;
  onAddMilestone?: (goalId: string) => void;
  onAddAction?: (milestoneId: string, goalId: string) => void;
  onDeleteGoal?: (id: string) => void;
  onDeleteMilestone?: (id: string) => void;
  onDeleteAction?: (id: string) => void;
  onPlanAction?: (action: ActionDataType) => void;
  defaultExpanded?: boolean;
  hasSelfTestResults?: boolean;
  hasCapabilityAssessment?: boolean;
  hasSparringSession?: boolean;
  onPhaseAction?: (phase: GoalPhase) => void;
}

/**
 * GoalCard
 * 
 * A calm, readable card for displaying a goal.
 * - Title (inline editable)
 * - Description (inline editable, multiline) — for context and scope determination
 * - Date range (inline editable)
 * - Completion indicator (calculated)
 * - Collapsible milestones underneath
 */
const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  milestones,
  actions,
  onUpdateGoal,
  onUpdateMilestone,
  onToggleAction,
  onUpdateAction,
  onAddMilestone,
  onAddAction,
  onDeleteGoal,
  onDeleteMilestone,
  onDeleteAction,
  onPlanAction,
  defaultExpanded = false,
  hasSelfTestResults = false,
  hasCapabilityAssessment = false,
  hasSparringSession = false,
  onPhaseAction,
}) => {
  // Auto-expand if sparring session exists and milestones were just created
  const shouldAutoExpand = hasSparringSession && milestones.length > 0;
  const [expanded, setExpanded] = useState(defaultExpanded || shouldAutoExpand);
  const [isHovered, setIsHovered] = useState(false);
  const [showDescription, setShowDescription] = useState(!!goal.description);
  const [showExecutionReadyCard, setShowExecutionReadyCard] = useState(false);

  // Get milestones for this goal
  const goalMilestones = useMemo(() => {
    return milestones.filter(m => m.goalId === goal.id);
  }, [milestones, goal.id]);

  // Focus detection - subtiele hints voor richting
  // Only use for Firebase types (Goal, Milestone, Action with required properties)
  const isFirebaseGoal = 'status' in goal && 'ownerId' in goal && 'createdAt' in goal;
  const firebaseMilestones = milestones.filter(m => 'ownerId' in m && 'createdAt' in m) as Milestone[];
  const firebaseActions = actions.filter(a => 'ownerId' in a && 'createdAt' in a) as Action[];
  const { focus, topFocusAction } = useFocusDetection(
    isFirebaseGoal ? (goal as Goal) : null,
    firebaseMilestones,
    firebaseActions,
    false  // geen autoDetect
  );

  // Get actions per milestone
  const getActionsForMilestone = (milestoneId: string) => {
    return actions.filter(a => a.milestoneId === milestoneId);
  };

  // Get orphan actions (actions without milestone but with this goal)
  const orphanActions = useMemo(() => {
    return actions.filter(a => !a.milestoneId && a.goalId === goal.id);
  }, [actions, goal.id]);

  // Calculate completion
  const isCompleted = useMemo(() => {
    const allActions = actions.filter(a => a.goalId === goal.id);
    if (allActions.length === 0) return false;
    return allActions.every(a => !!a.completed);
  }, [actions, goal.id]);

  // Format date range
  const formatDateRange = () => {
    if (!goal.startDate && !goal.endDate) return null;
    const start = goal.startDate ? new Date(goal.startDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '...';
    const end = goal.endDate ? new Date(goal.endDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '...';
    return `${start} → ${end}`;
  };

  // Only show phase indicator for Firebase goals (with status field)
  const isFirebaseGoalForPhase = 'status' in goal && 'ownerId' in goal;
  // Include all milestones and actions for this goal for phase detection
  const firebaseMilestonesForPhase = milestones.filter(m => 
    'ownerId' in m && 'createdAt' in m && m.goalId === goal.id
  ) as Milestone[];
  const firebaseActionsForPhase = actions.filter(a => 
    'ownerId' in a && 'createdAt' in a && a.goalId === goal.id
  ) as Action[];

  // Detect phase transition to 'execution' and show celebration card
  useEffect(() => {
    if (!isFirebaseGoalForPhase || goal.status !== 'committed') {
      return;
    }

    const currentPhase = detectCurrentPhase(
      goal as Goal,
      firebaseMilestonesForPhase,
      firebaseActionsForPhase,
      hasSelfTestResults,
      hasCapabilityAssessment,
      hasSparringSession
    );

    // Check if we just reached execution phase
    if (currentPhase === 'execution') {
      const storageKey = `executionReady_shown_${goal.id}`;
      const hasShownBefore = localStorage.getItem(storageKey);

      if (!hasShownBefore) {
        console.log('🚀 Phase transition detected: execution phase reached for goal', goal.id);
        setShowExecutionReadyCard(true);
        localStorage.setItem(storageKey, 'true');
      }
    }
  }, [
    goal.id,
    isFirebaseGoalForPhase ? (goal as Goal).status : undefined,
    isFirebaseGoalForPhase,
    firebaseMilestonesForPhase.length,
    firebaseActionsForPhase.length,
    firebaseActionsForPhase.filter(a => a.completed).length, // Track completion changes
    hasSelfTestResults,
    hasCapabilityAssessment,
    hasSparringSession,
  ]);

  return (
    <div
      className={`
        bg-white rounded-lg border border-gray-200
        shadow-sm hover:shadow-md
        transition-shadow duration-200
        overflow-hidden
      `.trim()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Phase Indicator (alleen voor committed goals) */}
      {isFirebaseGoalForPhase && goal.status === 'committed' && (
        <PhaseIndicator
          goal={goal as Goal}
          milestones={firebaseMilestonesForPhase}
          actions={firebaseActionsForPhase}
          hasSelfTestResults={hasSelfTestResults}
          hasCapabilityAssessment={hasCapabilityAssessment}
          hasSparringSession={hasSparringSession}
          onPhaseAction={onPhaseAction}
        />
      )}

      {/* Card header */}
      <div className="px-5 py-4">
        <div className="flex items-start justify-between">
          {/* Left side */}
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            {/* Goal icon */}
            <GoalIcon size={18} className="text-red-500 mt-0.5" />

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-1">
              {/* Title row */}
              <div className="flex items-center space-x-2">
                {(() => {
                  // Goal Type Badge (Fase 1)
                  const hasGoalType = 'goalType' in goal && goal.goalType && (goal.goalType as GoalTypeEnum) in GOAL_TYPE_BADGE;
                  return (
                    <>
                      {hasGoalType && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${GOAL_TYPE_BADGE[goal.goalType as GoalTypeEnum].color}`}>
                          {GOAL_TYPE_BADGE[goal.goalType as GoalTypeEnum].label}
                        </span>
                      )}
                      <InlineEditableText
                        value={goal.title}
                        onSave={(title) => onUpdateGoal(goal.id, { title })}
                        placeholder="Hoofddoel naam..."
                        className={`
                          text-base font-semibold flex-1
                          ${isCompleted ? 'text-gray-400' : 'text-gray-900'}
                        `}
                        as="h3"
                      />
                      {/* Committed indicator (Fase 1) */}
                      {'status' in goal && goal.status === 'committed' && (
                        <span className="text-xs text-green-600" title="Gecommitteerd">
                          ✓
                        </span>
                      )}
                      <CompletionDot completed={isCompleted} size="md" />
                    </>
                  );
                })()}
              </div>

              {/* Date range row */}
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <InlineEditableDate
                  value={goal.startDate || ''}
                  onSave={(date) => onUpdateGoal(goal.id, { startDate: date })}
                  placeholder="Start"
                  className="text-xs"
                />
                <span className="text-gray-300">→</span>
                <InlineEditableDate
                  value={goal.endDate || ''}
                  onSave={(date) => onUpdateGoal(goal.id, { endDate: date })}
                  placeholder="Eind"
                  className="text-xs"
                />
                {/* Missing dates indicator */}
                {(!goal.startDate || !goal.endDate) && 'status' in goal && goal.status === 'committed' && (
                  <span className="text-orange-500 text-xs ml-2 inline-flex items-center gap-1" title="Voeg datums toe om de tijdlijn te zien">
                    <WarningIcon size={14} className="text-orange-500" /> Voeg datums toe voor tijdlijn
                  </span>
                )}
              </div>

              {/* Focus indicator - subtiele hint voor richting */}
              {focus && (
                <div className="text-xs text-blue-600 mt-1">
                  Focus nu: {MILESTONE_CATEGORY_LABELS[focus.category]}
                </div>
              )}

              {/* Description section */}
              {showDescription ? (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <InlineEditableText
                    value={goal.description || ''}
                    onSave={(description) => onUpdateGoal(goal.id, { description })}
                    placeholder="Beschrijf je doel... Wat wil je bereiken? Hoe groot is de scope? Dit helpt bij het opdelen in mijlpalen en acties."
                    className="text-sm text-gray-600 leading-relaxed"
                    multiline
                  />
                </div>
              ) : (
                <button
                  onClick={() => setShowDescription(true)}
                  className={`
                    mt-2 text-xs text-gray-400 hover:text-gray-600
                    flex items-center space-x-1
                    transition-opacity duration-150
                    ${isHovered ? 'opacity-100' : 'opacity-0'}
                  `}
                >
                  <span>+</span>
                  <span>Beschrijving toevoegen</span>
                </button>
              )}
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-2 ml-4">
            {/* Milestone count badge */}
            {goalMilestones.length > 0 && (
              <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {goalMilestones.length} mijlpalen
              </span>
            )}

            {/* Delete button (hover-only) */}
            {onDeleteGoal && (
              <button
                onClick={() => onDeleteGoal(goal.id)}
                className={`
                  text-gray-400 hover:text-red-500
                  p-1 rounded
                  transition-all duration-150
                  ${isHovered ? 'opacity-100' : 'opacity-0'}
                `}
                title="Verwijder hoofddoel"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {/* Expand chevron */}
            <CollapseChevron
              expanded={expanded}
              onToggle={() => setExpanded(!expanded)}
              size="md"
            />
          </div>
        </div>
      </div>

      {/* Collapsible content */}
      {expanded && (
        <div className="px-5 pb-4 pt-0 border-t border-gray-100">
          {/* Sparring completion banner */}
          {hasSparringSession && goalMilestones.length > 0 && (() => {
            // Count only actions that belong to milestones (exclude orphan actions)
            const actionsForMilestones = goalMilestones.reduce(
              (sum, m) => sum + getActionsForMilestone(m.id).length, 
              0
            );
            return actionsForMilestones > 0;
          })() && (
            <div className="mt-4 mb-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 rounded-lg">
              <div className="flex items-start gap-3">
                <SuccessIcon size={24} className="text-green-500" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Je acties staan klaar!
                  </h4>
                  <p className="text-sm text-gray-700 mb-2">
                    {(() => {
                      // Count only actions that belong to milestones (exclude orphan actions)
                      const actionsForMilestones = goalMilestones.reduce(
                        (sum, m) => sum + getActionsForMilestone(m.id).length, 
                        0
                      );
                      return `We hebben ${actionsForMilestones} acti${actionsForMilestones === 1 ? 'e' : 'es'} gegenereerd voor je ${goalMilestones.length} mijlpa${goalMilestones.length === 1 ? 'al' : 'len'}.`;
                    })()}
                  </p>
                  <p className="text-xs text-gray-600">
                    👇 Vouw de mijlpalen hieronder uit om je acties te zien en aan de slag te gaan!
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Scrollable container for milestones/actions with visible scrollbar */}
          <div className="mt-4 space-y-2 overflow-y-auto pr-2 scrollbar-visible" style={{ maxHeight: 'clamp(200px, 40vh, 600px)' }}>
            <style>{`
              .scrollbar-visible::-webkit-scrollbar {
                width: 12px;
              }
              .scrollbar-visible::-webkit-scrollbar-track {
                background: #f1f5f9;
                border-radius: 6px;
              }
              .scrollbar-visible::-webkit-scrollbar-thumb {
                background: #cbd5e1;
                border-radius: 6px;
                border: 2px solid #f1f5f9;
              }
              .scrollbar-visible::-webkit-scrollbar-thumb:hover {
                background: #94a3b8;
              }
            `}</style>
            {/* Milestones */}
            {goalMilestones.length === 0 && orphanActions.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-2">
                Nog geen mijlpalen of acties
              </p>
            ) : (
              <>
                {goalMilestones.map((milestone, milestoneIndex) => {
                  // Auto-expand milestones from sparring so actions are visible
                  const isFromSparring = 'fromSparring' in milestone && milestone.fromSparring;
                  const hasActions = getActionsForMilestone(milestone.id).length > 0;
                  
                  // Calculate starting action number for this milestone
                  // Sum up all actions from previous milestones
                  const startActionNumber = goalMilestones
                    .slice(0, milestoneIndex)
                    .reduce((sum, m) => sum + getActionsForMilestone(m.id).length, 0) + 1;
                  
                  return (
                    <MilestoneRow
                      key={milestone.id}
                      milestone={milestone}
                      actions={getActionsForMilestone(milestone.id)}
                      onUpdateMilestone={onUpdateMilestone}
                      onToggleAction={onToggleAction}
                      onUpdateAction={onUpdateAction}
                      onAddAction={onAddAction ? (mId) => onAddAction(mId, goal.id) : undefined}
                      onDeleteAction={onDeleteAction}
                      onDeleteMilestone={onDeleteMilestone}
                      onPlanAction={onPlanAction}
                      defaultExpanded={isFromSparring && hasActions}
                      focusActionId={topFocusAction}
                      startActionNumber={startActionNumber}
                    />
                  );
                })}

                {/* Orphan actions (actions without milestone) */}
                {orphanActions.length > 0 && (() => {
                  // Calculate starting action number for orphan actions
                  // (after all milestone actions)
                  const totalMilestoneActions = goalMilestones.reduce(
                    (sum, m) => sum + getActionsForMilestone(m.id).length,
                    0
                  );
                  const orphanStartNumber = totalMilestoneActions + 1;
                  
                  return (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-2">Acties zonder mijlpaal:</p>
                      <div className="space-y-1 pl-4">
                        {orphanActions.map((action, index) => (
                          <ActionRow
                            key={action.id}
                            action={action}
                            onToggleComplete={onToggleAction}
                            onUpdateTitle={onUpdateAction}
                            onPlanInAgenda={onPlanAction}
                            onDelete={onDeleteAction}
                            actionNumber={orphanStartNumber + index}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {/* Add milestone - opens modal */}
            {onAddMilestone && (
              <button
                onClick={() => onAddMilestone(goal.id)}
                className="
                  w-full text-sm text-gray-400 hover:text-orange-600
                  py-2 mt-2 flex items-center justify-center space-x-1
                  border-t border-gray-100
                  transition-colors duration-150
                "
              >
                <span>+</span>
                <span>Nieuwe mijlpaal</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Execution Ready Celebration Card */}
      {showExecutionReadyCard && (
        <ExecutionReadyCard
          goalTitle={goal.title}
          totalMilestones={firebaseMilestonesForPhase.length}
          totalActions={firebaseActionsForPhase.length}
          onClose={() => setShowExecutionReadyCard(false)}
        />
      )}
    </div>
  );
};

export default GoalCard;
